import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  SvgIcon,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
} from '@mui/material';
import { Close, Delete } from '@mui/icons-material';
import { useStyles } from '../../../theme/styles.helpers';
import { AvailabilityModalStyles } from './AvailabilityModalStyles';
import { getWeekdayFromDate, SesamiAvailability, SesamiInterval } from '../../../pages/calendar/Calendar';

interface AvailabilityModalProps {
  selectedDate: string | null;
  availabilities: SesamiAvailability[];
  onClose: () => void;
  onCreateAvailability?: (availability: SesamiAvailability) => void;
  onRemoveAvailability?: (availability: SesamiAvailability, mode?: 'single' | 'all') => void; // new
}

const AvailabilityModal: FC<AvailabilityModalProps> = ({
  selectedDate,
  availabilities,
  onClose,
  onCreateAvailability,
  onRemoveAvailability,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(AvailabilityModalStyles, {});
  const [availabilityType, setAvailabilityType] = useState<'specific' | 'recurring'>('specific');
  const [intervals, setIntervals] = useState<SesamiInterval[]>([{ from: '09:00', to: '17:00', unavailable: false }]);

  if (!selectedDate) return null;

  // Check if selectedDate has an explicit exclusion
  const hasExplicitExclusion = availabilities.some(
    (a) => a.date === selectedDate && (!a.intervals || a.intervals.length === 0)
  );

  // Filter availabilities for the selected date
  const dayAvailabilities = availabilities.filter((availability) => {
    const isSpecificDate = availability.date === selectedDate;
    const isRecurring = availability.weekday && getWeekdayFromDate(selectedDate) === availability.weekday;

    // If explicit exclusion exists, show nothing at all (no recurring, no intervals)
    if (hasExplicitExclusion) {
      return isSpecificDate && availability.intervals.length > 0;
    }

    // Otherwise include recurring and specific
    if (isSpecificDate && (!availability.intervals || availability.intervals.length === 0)) {
      return false;
    }

    return isSpecificDate || isRecurring;
  });

  // Format time for display
  const formatTime = (timeString: string): string => {
    return timeString.substring(0, 5); // Remove seconds if present
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle adding new interval
  const addInterval = () => {
    setIntervals([...intervals, { from: '09:00', to: '17:00', unavailable: false }]);
  };

  // Handle removing interval
  const removeInterval = (index: number) => {
    if (intervals.length > 1) {
      setIntervals(intervals.filter((_, i) => i !== index));
    }
  };

  // Handle interval change
  const updateInterval = (index: number, field: keyof SesamiInterval, value: string | boolean) => {
    const newIntervals = [...intervals];
    newIntervals[index] = { ...newIntervals[index], [field]: value };
    setIntervals(newIntervals);
  };

  // Handle form submission
  const handleCreateAvailability = () => {
    if (intervals.length === 0) return;

    const newAvailability: SesamiAvailability = {
      intervals: intervals.filter((interval) => interval.from && interval.to),
      date: null,
      weekday: null,
      type: 'date',
    };

    if (availabilityType === 'specific') {
      newAvailability.date = selectedDate;
      newAvailability.type = 'date';
    } else {
      newAvailability.weekday = getWeekdayFromDate(selectedDate);
      newAvailability.type = 'wday';
    }

    onCreateAvailability?.(newAvailability);
    setIntervals([{ from: '09:00', to: '17:00', unavailable: false }]);
  };

  return (
    <>
      <Box sx={styles.root}>
        <Box sx={styles.container}>
          <Box sx={styles.header}>
            <Box sx={styles.headerTitle}>
              <Typography variant="h4">
                {t('Availabilities for')} {formatDate(selectedDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={styles.details}>
            <Box sx={styles.leftContainer}>
              {dayAvailabilities.length === 0 ? (
                <Typography variant="body1" color="text.secondary">
                  {t('No availabilities found for this date')}
                </Typography>
              ) : (
                <List sx={{ margin: 0, padding: 0 }}>
                  {dayAvailabilities.map((availability, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="h4" gutterBottom>
                        {availability.date ? t('Specific Date') : t('Recurring')}{' '}
                        {availability.weekday && `(${t(availability.weekday)})`}
                      </Typography>

                      {availability.intervals.map((interval, intervalIndex) => (
                        <ListItem key={intervalIndex} sx={{ pl: 0 }}>
                          <ListItemText
                            primary={`${formatTime(interval.from)} - ${formatTime(interval.to)}`}
                            secondary={interval.unavailable ? t('Unavailable') : t('Available')}
                          />
                          <Chip
                            label={interval.unavailable ? t('Unavailable') : t('Available')}
                            color={interval.unavailable ? 'error' : 'success'}
                            size="small"
                          />
                        </ListItem>
                      ))}

                      {/* Action buttons */}
                      <Box display="flex" gap={1} justifyContent="space-between" alignItems="center" mt={1}>
                        <Typography>{t('Delete')}:</Typography>
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          {availability.date ? (
                            // Specific date → only one action
                            <Button
                              sx={styles.button}
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => onRemoveAvailability?.(availability, 'single')}
                            >
                              {t('Remove Availability')}
                            </Button>
                          ) : (
                            // Recurring → two choices
                            <>
                              <Button
                                sx={styles.button}
                                variant="outlined"
                                size="small"
                                onClick={() => onRemoveAvailability?.(availability, 'single')}
                              >
                                {t('This date only')}
                              </Button>
                              <Button
                                sx={styles.button}
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => onRemoveAvailability?.(availability, 'all')}
                              >
                                {t('All occurrences')}
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </List>
              )}
            </Box>

            <Box sx={styles.rightContainer}>
              <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'} height={'100%'}>
                <Box>
                  <Typography variant="h4" gutterBottom>
                    {t('Create New Availability')}
                  </Typography>

                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      value={availabilityType}
                      onChange={(e) => setAvailabilityType(e.target.value as 'specific' | 'recurring')}
                      row
                    >
                      <FormControlLabel
                        value="specific"
                        sx={styles.radioButton}
                        control={<Radio />}
                        label={t('Today')}
                      />
                      <FormControlLabel
                        value="recurring"
                        sx={styles.radioButton}
                        control={<Radio />}
                        label={`${t('Every')} ${t(getWeekdayFromDate(selectedDate))}`}
                      />
                    </RadioGroup>
                  </FormControl>

                  <Typography variant="subtitle2" gutterBottom>
                    {t('Time Intervals')}
                  </Typography>

                  <Box>
                    {intervals.map((interval, index) => (
                      <Box key={index} display={'flex'} gap={3} mb={2}>
                        <TextField
                          label={t('From')}
                          type="time"
                          value={interval.from}
                          onChange={(e) => updateInterval(index, 'from', e.target.value)}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label={t('To')}
                          type="time"
                          value={interval.to}
                          onChange={(e) => updateInterval(index, 'to', e.target.value)}
                          size="small"
                          fullWidth
                        />
                        <FormControlLabel
                          sx={{ margin: 0 }}
                          control={
                            <Chip
                              label={interval.unavailable ? t('Unavailable') : t('Available')}
                              color={interval.unavailable ? 'error' : 'success'}
                              onClick={() => updateInterval(index, 'unavailable', !interval.unavailable)}
                              clickable
                            />
                          }
                          label=""
                        />
                        <IconButton
                          onClick={() => removeInterval(index)}
                          disabled={intervals.length === 1}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    ))}

                    <Box display={'flex'} justifyContent={'end'}>
                      <Button sx={styles.button} variant="outlined" onClick={addInterval} size="small">
                        {t('Add Interval')}
                      </Button>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    sx={styles.button}
                    variant="contained"
                    onClick={handleCreateAvailability}
                    disabled={intervals.length === 0}
                  >
                    {t('Create Availability')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Closing Icon */}
        <Box onClick={onClose} sx={{ marginLeft: 2, cursor: 'pointer' }}>
          <SvgIcon sx={{ color: 'primary.main' }} component={Close}></SvgIcon>
        </Box>
      </Box>
    </>
  );
};

export default AvailabilityModal;
